import dayjs from 'dayjs';

const propToString = <T>() => {
	return new Proxy(
		{},
		{
			get: (_, prop) => prop,
		}
	) as { [K in keyof T]: K };
};

interface FormatParams {
	locale?: string;
	currency?: string;
	rounding?: boolean;
}

const formatCurrency = (
	amount: number,
	{ locale = 'en-US', currency = 'USD', rounding = false }: FormatParams
) => {
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: currency,
		minimumFractionDigits: rounding ? 0 : 2,
		maximumFractionDigits: rounding ? 0 : 2,
	}).format(amount);
};

const formatCurrencyCompact = (
	amount: number,
	{ locale = 'en-US', currency = 'USD' }: FormatParams = {}
) => {
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: currency,
		notation: 'compact',
		compactDisplay: 'short',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};

// Helper function to format Date objects to ISO string format (YYYY-MM-DD)
export const formatDateValue = (value: string | Date | number | undefined): string | null => {
	if (!value) return null;
	if (typeof value === 'string') return value;
	if (value instanceof Date) {
		return dayjs(value).format('YYYY-MM-DD');
	}
	// Handle number (timestamp) - though date fields should use Date objects
	if (typeof value === 'number') {
		return dayjs(value).format('YYYY-MM-DD');
	}
	return null;
};

// Utility function to check error object for specific messagefunction isInvalidSearchTermError(error: unknown): boolean {
export function isInvalidSearchTermError(error: unknown): boolean {
	const INVALID_SEARCH_TERM_MESSAGE = 'Invalid searchTerm format.';
	if (!error || typeof error !== 'object') return false;
	if (!('data' in error)) return false;
	const data = (error as { data?: any }).data;
	if (!data || typeof data !== 'object') return false;
	if (!('message' in data)) return false;
	const message = data.message;
	return typeof message === 'string' && message.includes(INVALID_SEARCH_TERM_MESSAGE);
}

// Helper to format date
const formatDate = (dateValue: any) => {
	if (!dateValue) return '-';
	const dateObj = new Date(dateValue);
	if (isNaN(dateObj.getTime())) return '-';
	const day = dateObj.getDate();
	const month = dateObj.toLocaleString('en-US', { month: 'long' });
	const year = dateObj.getFullYear();
	return `${day} ${month} ${year}`;
};

// Helper to sanitize date string to 'YYYY-MM-DD'
const sanitizeDate = (dateStr: string): string => {
	const d = dayjs(dateStr);
	return d.isValid() ? d.format('YYYY-MM-DD') : dateStr;
};

/** Helper function that generates the content for a CSV file.
 *
 * @param data - Array of flat objects that get converted in a row
 * @param headers - Ordered array of [key, label] tuples
 */
export const generateCSV = <Data extends Record<any, any>>(
	data: Data[],
	headers: [keyof Data, string][]
) => {
	const formulaRegex = /^[=+\-@\t\r]/;
	const quoteRequiredRegex = /[",\r\n]/;
	// Helper to escape CSV values
	const escapeCsv = (value: string) => {
		if (value == null) return '';
		let str = String(value);
		// escape formula values
		if (formulaRegex.test(str)) {
			str = `'${str}`;
		}
		// RFC 4180: wrap in quotes if value contains comma, quote, CR, or LF;
		// double any embedded quotes
		if (quoteRequiredRegex.test(str)) {
			str = `"${str.replace(/"/g, '""')}"`;
		}
		return str;
	};

	// header rows
	const csvRows = [headers.map(([_, label]) => label).join(',')];
	// body rows
	for (const entry of data) {
		const row = headers.map(([key]) => escapeCsv(entry[key] ?? ''));
		csvRows.push(row.join(','));
	}

	return csvRows.join('\r\n');
};

/**
 * Validates response object from API and checks MIME type.
 * @param response - The response object from fetchWithBQ
 * @param expectedTypes - Expected MIME type(s) as string or array of strings
 * @returns Object with either data (blob) or error property
 */
export function validateBlobResponse(
	response: any,
	expectedTypes: string | string[] = ['text/csv', 'application/csv']
): { data?: Blob; error?: any } {
	const blob = response.data as Blob;
	const normalizeMimeType = (mimeType: string | undefined): string =>
		(mimeType ?? '').split(';')[0].trim().toLowerCase();
	const expectedBaseTypes = new Set(
		(Array.isArray(expectedTypes) ? expectedTypes : [expectedTypes]).map((type) =>
			normalizeMimeType(type)
		)
	);
	const permissiveDownloadTypes = new Set([
		'application/octet-stream',
		'text/plain',
		'application/vnd.ms-excel',
	]);
	const actualType = normalizeMimeType(blob?.type);

	if (
		!response.error &&
		(actualType === '' ||
			expectedBaseTypes.has(actualType) ||
			permissiveDownloadTypes.has(actualType))
	) {
		return { data: blob };
	} else {
		return response.error
			? { error: response.error }
			: {
					error: {
						status: 'ERROR',
						data: 'Unexpected file type received. Please try again later.',
					},
				};
	}
}

export const PropToString = propToString;
export const FormatCurrency = formatCurrency;
export const FormatCurrencyCompact = formatCurrencyCompact;
export const FormatDate = formatDate;
export const SanitizeDate = sanitizeDate;
